import { useState } from 'react';
import { BASE_URL } from '../../config';
import { showSuccess, showError } from '../../utils/dialog';
import AddressAutocomplete from '../AddressAutocomplete';

const OfficeModal = ({ office, onClose, onSave }) => {
  const [form, setForm] = useState({
    name: office?.name || '',
    address: office?.address || ''
  });
  const [hideFormWhileManual, setHideFormWhileManual] = useState(false);
  const [saving, setSaving] = useState(false);

  const formatAddress = (val) => {
    if (!val) return '';
    if (typeof val === 'string') return val;
    const addressParts = [val.address2 || val.line2, val.street, val.city, val.region, val.zip, val.country].filter(Boolean);
    if (val.name) {
      return `${val.name} - ${addressParts.join(', ')}`;
    }
    return addressParts.join(', ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const name = typeof form.name === 'string' ? form.name : (form.name?.name || '');
    const address = form.address; // Keep as is (object or string)

    if (typeof address === 'string' && !address.trim()) return showError('Address is required');
    if (typeof address === 'object' && !address) return showError('Address is required');
    if (!name.trim()) return showError('Name is required');

    try {
      setSaving(true);
      const url = office
        ? `${BASE_URL}/api/admin/offices/${office.id}`
        : `${BASE_URL}/api/admin/offices`;

      const method = office ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ ...form, name, address })
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to save office');

      showSuccess(office ? 'Office updated successfully' : 'Office created successfully');
      onSave();
      onClose();
    } catch (e) {
      showError('Error: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center px-3" style={{ zIndex: hideFormWhileManual ? 20 : 50 }}>
      <div className="bg-card text-foreground rounded-lg shadow w-full max-w-lg max-h-[90vh] overflow-hidden border border-border">
        {!hideFormWhileManual && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <h3 className="text-lg font-semibold">
              {office ? 'Edit Office' : 'Create Office'}
            </h3>
            <button className="text-muted-foreground hover:text-foreground" onClick={onClose}>✖</button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-4 space-y-4" style={{ display: hideFormWhileManual ? 'none' : 'block' }}>
          <div>
            <label className="block text-sm font-medium mb-2">Name *</label>
            <input
              type="text"
              value={typeof form.name === 'string' ? form.name : (form.name?.name || '')}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              className="w-full border border-border rounded-lg px-3 py-2 bg-white text-foreground"
              placeholder="Enter office name"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Address *</label>
            <div style={{ display: hideFormWhileManual ? 'none' : 'block' }}>
              <AddressAutocomplete
                value={form.address}
                onChange={(addr) => setForm((prev) => ({ ...prev, address: addr }))}
                onManualOpen={() => setHideFormWhileManual(true)}
                onManualClose={() => setHideFormWhileManual(false)}
                manualInitialName={typeof form.name === 'string' ? form.name : (form.name?.name || '')}
                onManualSave={(data) => setForm((prev) => ({ ...prev, address: { ...data.address, name: data.name } }))}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary"
            >
              Cancel
            </button>
            <button
              disabled={saving}
              type="submit"
              className="px-4 py-2 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-lg disabled:opacity-50"
            >
              {saving ? 'Saving...' : (office ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OfficeModal;